import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SAAS_ENABLED, SELF_SIGNUP_ENABLED } from '@edition/ui'
import { LandingPage } from '@edition/pages'
import { useAuth } from './auth/AuthProvider'
import { useUserContext } from './providers/UserContextProvider'
import { usePermissions } from './hooks/usePermissions'
import { Layout } from './components/Layout'
import { SuperAdminRoute } from './components/SuperAdminRoute'
import { LoadingState, AccessDeniedState, MissingTenantState, NoActiveMembershipState } from './components/ui/PageState'
import { AdminLayout } from './components/AdminLayout'
import { UnitAddress } from './features/dashboard/UnitAddress'

const Dashboard = lazy(() => import('./features/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })))
const DivisionCommandCenterRoute = lazy(() => import('./features/dashboard/EntityCommandCenterRoutes').then((m) => ({ default: m.DivisionCommandCenterRoute })))
const ValueStreamCommandCenterRoute = lazy(() => import('./features/dashboard/EntityCommandCenterRoutes').then((m) => ({ default: m.ValueStreamCommandCenterRoute })))
const OrganizationCommandCenterRoute = lazy(() => import('./features/dashboard/EntityCommandCenterRoutes').then((m) => ({ default: m.OrganizationCommandCenterRoute })))
const UnitRoute = lazy(() => import('./features/dashboard/UnitRoute').then((m) => ({ default: m.UnitRoute })))
const OrganizationsList = lazy(() => import('./features/organizations/OrganizationsList').then((m) => ({ default: m.OrganizationsList })))
const BusinessOpportunitiesList = lazy(() => import('./features/business-opportunities/BusinessOpportunitiesList').then((m) => ({ default: m.BusinessOpportunitiesList })))
const UsersList = lazy(() => import('./features/users/UsersList').then((m) => ({ default: m.UsersList })))
const TeamPage = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })))
const UniversalTeamPage = lazy(() => import('./features/teams/UniversalTeamPage').then((m) => ({ default: m.UniversalTeamPage })))
const ApprovalsPage = lazy(() => import('./features/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })))
const SetupWizard = lazy(() => import('./features/setup/SetupWizard').then((m) => ({ default: m.SetupWizard })))
const StrategyPage = lazy(() => import('./features/strategy/StrategyPage').then((m) => ({ default: m.StrategyPage })))
const DirectoryPage = lazy(() => import('./features/directory/DirectoryPage').then((m) => ({ default: m.DirectoryPage })))
const CheckInPage = lazy(() => import('./features/check-ins/CheckInPage').then((m) => ({ default: m.CheckInPage })))
const PeriodsAdminPage = lazy(() => import('./features/periods/PeriodsAdminPage').then((m) => ({ default: m.PeriodsAdminPage })))
const PeriodClosePage = lazy(() => import('./features/periods/PeriodClosePage').then((m) => ({ default: m.PeriodClosePage })))
const MobilePage = lazy(() => import('./pages/MobilePage').then((m) => ({ default: m.MobilePage })))
const ApiClientsPage = lazy(() => import('./features/api-clients/ApiClientsPage').then((m) => ({ default: m.ApiClientsPage })))
const AuditLogPage = lazy(() => import('./features/audit/AuditLogPage').then((m) => ({ default: m.AuditLogPage })))
const PlatformUsersPage = lazy(() => import('./features/platform/PlatformUsersPage').then((m) => ({ default: m.PlatformUsersPage })))
const AiSettingsPage = lazy(() => import('./pages/AiSettingsPage').then((m) => ({ default: m.AiSettingsPage })))
const TerminologyPage = lazy(() => import('./pages/TerminologyPage').then((m) => ({ default: m.TerminologyPage })))
const OperatingModelPage = lazy(() => import('./pages/OperatingModelPage').then((m) => ({ default: m.OperatingModelPage })))
const IdentitySettingsPage = lazy(() => import('./features/identity/IdentitySettingsPage').then((m) => ({ default: m.IdentitySettingsPage })))
const PreferencesPage = lazy(() => import('./features/preferences/PreferencesPage').then((m) => ({ default: m.PreferencesPage })))
const AcceptInvitationPage = lazy(() => import('./features/users/AcceptInvitationPage').then((m) => ({ default: m.AcceptInvitationPage })))
const StakeholderNewsfeedPage = lazy(() => import('./features/stakeholder-status/StakeholderNewsfeedPage').then((m) => ({ default: m.StakeholderNewsfeedPage })))
const AdminHome = lazy(() => import('./features/admin/AdminHome').then((m) => ({ default: m.AdminHome })))
const AdminUpdatesPage = lazy(() => import('./features/admin/AdminUpdatesPage').then((m) => ({ default: m.AdminUpdatesPage })))
const BillingPage = lazy(() => import('@edition/pages').then((m) => ({ default: m.BillingPage! })))
const TrialExpiredPage = lazy(() => import('@edition/pages').then((m) => ({ default: m.TrialExpiredPage! })))
const FeedbackUpdateReceiptPage = lazy(() => import('@edition/pages').then((m) => ({ default: m.FeedbackUpdateReceiptPage! })))
const FeedbackQueuePage = lazy(() => import('@edition/pages').then((m) => ({ default: m.FeedbackQueuePage! })))
const FeedbackDetailPage = lazy(() => import('@edition/pages').then((m) => ({ default: m.FeedbackDetailPage! })))

const SignupPage = SELF_SIGNUP_ENABLED
  ? lazy(() => import('./features/signup/SignupPage').then((m) => ({ default: m.SignupPage })))
  : null

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()

  if (isLoading) {
    return (
      <LoadingState
        className="min-h-screen"
        title={t('states.loadingWorkspaceTitle')}
        description={t('states.loadingWorkspaceDescription')}
      />
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ returnUrl: location.pathname + location.search + location.hash }} />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { hasOrgAdminAccess, isLoading, error, refetch, userDetail, activeMemberships } = useUserContext()
  const { hasRole } = useAuth()
  const { t } = useTranslation()

  if (isLoading) {
    return <LoadingState title={t('states.loadingPageTitle')} description={t('states.loadingWorkspaceDescription')} />
  }

  if (error) {
    return <MissingTenantState onRetry={refetch} />
  }

  // A platform super admin holds no workspace membership by design. Checked
  // after loading and before memberships, or the one account allowed into the
  // audit log was told to "ask an admin to add you" on every admin page.
  if (hasRole('super_admin')) {
    return <>{children}</>
  }

  if (userDetail && activeMemberships.length === 0) {
    return <NoActiveMembershipState />
  }

  if (!hasOrgAdminAccess) {
    return (
      <AccessDeniedState
        title={t('states.adminAccessTitle')}
        description={t('states.adminAccessDescription')}
        requiredRole="ADMIN / OWNER"
      />
    )
  }

  return <>{children}</>
}

function ApproverRoute({ children }: { children: React.ReactNode }) {
  const { hasOrgAdminAccess, isLoading, error, refetch, userDetail, activeMemberships } = useUserContext()
  const { canApprove } = usePermissions()
  const { t } = useTranslation()

  if (isLoading) {
    return <LoadingState title={t('states.loadingPageTitle')} description={t('states.loadingWorkspaceDescription')} />
  }

  if (error) {
    return <MissingTenantState onRetry={refetch} />
  }

  if (userDetail && activeMemberships.length === 0) {
    return <NoActiveMembershipState />
  }

  if (!hasOrgAdminAccess && !canApprove) {
    return (
      <AccessDeniedState
        title={t('states.approverAccessTitle')}
        description={t('states.approverAccessDescription')}
        requiredRole="MANAGER / ADMIN / OWNER"
      />
    )
  }

  return <>{children}</>
}

/**
 * A period close-out scores and carries forward every objective in the organization,
 * so it belongs to the organization leader as much as to the admin track — see
 * `usePermissions().canClosePeriod`.
 */
function PeriodCloseRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, error, refetch, userDetail, activeMemberships } = useUserContext()
  const { canClosePeriod } = usePermissions()
  const { t } = useTranslation()

  if (isLoading) {
    return <LoadingState title={t('states.loadingPageTitle')} description={t('states.loadingWorkspaceDescription')} />
  }

  if (error) {
    return <MissingTenantState onRetry={refetch} />
  }

  if (userDetail && activeMemberships.length === 0) {
    return <NoActiveMembershipState />
  }

  if (!canClosePeriod) {
    return (
      <AccessDeniedState
        title={t('states.periodCloseAccessTitle')}
        description={t('states.periodCloseAccessDescription')}
        requiredRole="ORGANIZATION MANAGER / ADMIN / OWNER"
      />
    )
  }

  return <>{children}</>
}

function CommandCenterTabRedirect({ tab }: { tab: 'work' | 'approvals' }) {
  const { primaryContext } = useUserContext()
  const location = useLocation()
  const previous = new URLSearchParams(location.search)
  // Forward every param (deep links like objectiveId must survive the hop —
  // audit F-08/IA-02); only 'tab' is remapped to the command center's workTab.
  const next = new URLSearchParams(previous)
  next.set('tab', tab)
  if (tab === 'work' && previous.get('tab')) {
    next.set('workTab', previous.get('tab')!)
  }

  const base = primaryContext?.entityType === 'PRODUCT_TEAM' && primaryContext.productTeamId
    ? `/teams/${primaryContext.productTeamId}`
    : '/'
  return <Navigate to={`${base}?${next.toString()}`} replace />
}

/** Legacy /objectives links carried filters and deep links — forward them (F-08/IA-02). */
function ObjectivesRedirect() {
  const location = useLocation()
  const next = new URLSearchParams(location.search)
  next.set('tab', 'objectives')
  return <Navigate to={`/work?${next.toString()}`} replace />
}

function TeamTabRedirect({ tab }: { tab: 'roadmap' | 'stakeholders' }) {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const next = new URLSearchParams(location.search)
  next.set('tab', tab)
  return <Navigate to={`/teams/${id}?${next.toString()}`} replace />
}

function HierarchyTeamTabRedirect({ segment }: { segment: 'divisions' | 'value-streams' }) {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/${segment}/${id}/team?tab=stakeholders`} replace />
}

function StrategyTabRedirect({ tab }: { tab: 'flow' | 'cascade' }) {
  const location = useLocation()
  const next = new URLSearchParams(location.search)
  next.set('tab', tab)
  return <Navigate to={`/strategy?${next.toString()}`} replace />
}

function OidcCallbackRedirect() {
  const destination = SAAS_ENABLED || window.location.pathname === '/oidc/callback'
    ? '/' : window.location.pathname + window.location.search + window.location.hash
  return <Navigate to={destination} replace />
}

function App() {
  const { t } = useTranslation()

  return (
    <Suspense fallback={<LoadingState title={t('states.loadingPageTitle')} description={t('states.loadingWorkspaceDescription')} />}>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="teams/:id" element={<UnitAddress><TeamPage /></UnitAddress>} />
          <Route path="teams/:id/roadmap" element={<TeamTabRedirect tab="roadmap" />} />
          <Route path="teams/:id/status" element={<TeamTabRedirect tab="stakeholders" />} />
          <Route path="teams/:id/team" element={<UnitAddress><UniversalTeamPage type="product-team" /></UnitAddress>} />
          {/* One address for any unit, at any level. The three typed routes
              below between them can only name three levels, so a tenant with a
              fourth had units nothing could link to. */}
          <Route path="units/:ref" element={<UnitRoute />} />
          <Route path="organization" element={<OrganizationCommandCenterRoute />} />
          <Route path="divisions/:id" element={<UnitAddress><DivisionCommandCenterRoute /></UnitAddress>} />
          <Route path="value-streams/:id" element={<UnitAddress><ValueStreamCommandCenterRoute /></UnitAddress>} />
          <Route path="divisions/:id/team" element={<UnitAddress><UniversalTeamPage type="division" /></UnitAddress>} />
          <Route path="divisions/:id/status" element={<HierarchyTeamTabRedirect segment="divisions" />} />
          <Route path="value-streams/:id/team" element={<UnitAddress><UniversalTeamPage type="value-stream" /></UnitAddress>} />
          <Route path="value-streams/:id/status" element={<HierarchyTeamTabRedirect segment="value-streams" />} />
          <Route path="work" element={<CommandCenterTabRedirect tab="work" />} />
          <Route path="objectives" element={<ObjectivesRedirect />} />
          <Route path="organizations/:id/status" element={<Navigate to="/organization?tab=stakeholders" replace />} />
          <Route path="business-opportunities" element={<BusinessOpportunitiesList />} />
          <Route path="strategy" element={<StrategyPage />} />
          <Route path="strategy-cascade" element={<StrategyTabRedirect tab="cascade" />} />
          <Route path="directory" element={<DirectoryPage />} />
          <Route path="newsfeed" element={<StakeholderNewsfeedPage />} />
          <Route path="updates" element={<Navigate to="/newsfeed" replace />} />
          {SAAS_ENABLED && <Route path="feedback/updates/:notificationId" element={<FeedbackUpdateReceiptPage />} />}
          <Route path="check-in" element={<CheckInPage />} />
          <Route path="approvals" element={<ApproverRoute><CommandCenterTabRedirect tab="approvals" /></ApproverRoute>} />
          <Route path="periods/:id/close" element={<PeriodCloseRoute><PeriodClosePage /></PeriodCloseRoute>} />
          <Route path="flowmap" element={<StrategyTabRedirect tab="flow" />} />
          <Route path="strategy-sankey" element={<StrategyTabRedirect tab="flow" />} />
          <Route path="preferences" element={<PreferencesPage />} />
        </Route>
        {/* Legacy admin paths — redirect into the console before any layout mounts */}
        <Route path="/organizations" element={<Navigate to="/admin/organizations" replace />} />
        <Route path="/users" element={<Navigate to="/admin/users" replace />} />
        {SAAS_ENABLED && <Route path="/billing" element={<Navigate to="/admin/billing" replace />} />}
        <Route path="/identity" element={<Navigate to="/admin/identity" replace />} />
        <Route path="/api-clients" element={<Navigate to="/admin/api-clients" replace />} />
        <Route path="/audit-log" element={<Navigate to="/admin/audit-log" replace />} />
        <Route path="/ai-settings" element={<Navigate to="/admin/ai-settings" replace />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminHome />} />
          <Route path="updates" element={<AdminUpdatesPage />} />
          {SAAS_ENABLED && <Route path="updates/:notificationId" element={<FeedbackUpdateReceiptPage backTo="/admin/updates" />} />}
          <Route path="organizations" element={<OrganizationsList />} />
          <Route path="users" element={<UsersList />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          {SAAS_ENABLED && <Route path="billing" element={<BillingPage />} />}
          <Route path="identity" element={<IdentitySettingsPage />} />
          <Route path="api-clients" element={<ApiClientsPage />} />
          <Route path="audit-log" element={<SuperAdminRoute><AuditLogPage /></SuperAdminRoute>} />
          {SAAS_ENABLED && <Route path="platform-users" element={<SuperAdminRoute><PlatformUsersPage /></SuperAdminRoute>} />}
          {SAAS_ENABLED && <Route path="feedback" element={<SuperAdminRoute><FeedbackQueuePage /></SuperAdminRoute>} />}
          {SAAS_ENABLED && <Route path="feedback/:id" element={<SuperAdminRoute><FeedbackDetailPage /></SuperAdminRoute>} />}
          <Route path="ai-settings" element={<AiSettingsPage />} />
          <Route path="terminology" element={<TerminologyPage />} />
          <Route path="operating-model" element={<OperatingModelPage />} />
          <Route path="periods" element={<PeriodsAdminPage />} />
        </Route>
        <Route
          path="/mobile"
          element={
            <ProtectedRoute>
              <MobilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/setup"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <SetupWizard />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<LandingPage />} />
        <Route path="/oidc/callback" element={<ProtectedRoute><OidcCallbackRedirect /></ProtectedRoute>} />
        {SELF_SIGNUP_ENABLED && SignupPage && (
          <Route
            path="/signup"
            element={<SignupPage />}
          />
        )}
        <Route path="/invite/accept" element={<AcceptInvitationPage />} />
        {SAAS_ENABLED && <Route
          path="/trial-expired"
          element={
            <ProtectedRoute>
              <TrialExpiredPage />
            </ProtectedRoute>
          }
        />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App
